import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Stack, TextField } from '@mui/material';
import type { Editor } from '@tiptap/react';
import React, { useRef } from 'react';
import { Palette } from 'Palette';
import type { AppPostFormValues, NewsItemAdmin } from '../../../models/NewsItem';
import { SECONDARY_BUTTON_HOVER_SX, StyledButton } from '../../../components/styles/StyledButtons';
import { StyledHeading, StyledTypography } from '../../../components/styles/styles';
import InlineRichText from '../../../components/admin/newContentManagement/InlineRichText';
import { useAppPostForm } from './useAppPostForm';

type ToastType = 'success' | 'info' | 'warning' | 'error';
type Props = {
  editing: NewsItemAdmin | null;
  onClose: () => void;
  onSaveDraft: (values: AppPostFormValues) => Promise<void>;
  onPublish: (values: AppPostFormValues) => Promise<void>;
  onShowToast: (message: string, type: ToastType) => void;
};
type Form = ReturnType<typeof useAppPostForm>;

const ARTICLE_BOX_SX = {
  minHeight: 320,
  p: 1,
  border: `1px solid ${Palette.default.border}`,
  borderRadius: 1,
  cursor: 'text',
};

function FormActions({ form, onClose }: { form: Form; onClose: () => void }) {
  const busy = form.saving || form.publishing;
  return (
    <>
      <StyledButton
        noTopSpacing
        buttonVariant="inactiveOrCancel"
        sx={SECONDARY_BUTTON_HOVER_SX}
        onClick={onClose}
      >
        Cancel
      </StyledButton>
      <StyledButton
        noTopSpacing
        buttonVariant={form.isDraftOrNew ? 'inactiveOrCancel' : 'default'}
        sx={form.isDraftOrNew ? SECONDARY_BUTTON_HOVER_SX : undefined}
        onClick={() => void form.saveDraft()}
        disabled={!form.canSaveDraft || busy}
      >
        {form.saving ? 'Saving…' : form.isDraftOrNew ? 'Save draft' : 'Save'}
      </StyledButton>
      {form.isDraftOrNew && (
        <StyledButton
          noTopSpacing
          onClick={() => void form.publish()}
          disabled={!form.canPublish || busy}
        >
          {form.publishing ? 'Publishing…' : 'Publish'}
        </StyledButton>
      )}
    </>
  );
}

function ImageField({ form }: { form: Form }) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  return (
    <Box>
      <StyledTypography fontSize="0.875rem" sx={{ mb: 0.5, color: Palette.default.disabledText }}>
        Image
      </StyledTypography>
      {form.values.imageUrl && (
        <Box
          component="img"
          src={form.values.imageUrl}
          alt=""
          sx={{
            width: '100%',
            maxHeight: 160,
            objectFit: 'cover',
            borderRadius: 1,
            mb: 1,
            display: 'block',
          }}
        />
      )}
      <Stack direction="row" spacing={1}>
        <StyledButton
          noTopSpacing
          buttonVariant="inactiveOrCancel"
          sx={SECONDARY_BUTTON_HOVER_SX}
          onClick={() => imageInputRef.current?.click()}
          disabled={form.uploadingImage}
          startIcon={form.uploadingImage ? <CircularProgress size={16} /> : undefined}
        >
          {form.values.imageUrl ? 'Replace image' : 'Upload image'}
        </StyledButton>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void form.handleImageFile(file);
            event.target.value = '';
          }}
        />
        {form.values.imageUrl && (
          <StyledButton
            noTopSpacing
            buttonVariant="inactiveOrCancel"
            sx={SECONDARY_BUTTON_HOVER_SX}
            onClick={() => form.setValues((v) => ({ ...v, imageUrl: '' }))}
          >
            Remove
          </StyledButton>
        )}
      </Stack>
    </Box>
  );
}

function ArticleField({ form }: { form: Form }) {
  const editorRef = useRef<Editor | null>(null);
  return (
    <Box>
      <StyledTypography fontSize="0.875rem" sx={{ mb: 0.5, color: Palette.default.disabledText }}>
        Article
      </StyledTypography>
      <Box
        sx={ARTICLE_BOX_SX}
        onClick={(event) => {
          if (event.target === event.currentTarget) editorRef.current?.commands.focus('end');
        }}
      >
        <InlineRichText
          preset="article"
          html={form.values.article}
          onChange={(html) => form.setValues((v) => ({ ...v, article: html }))}
          editorRef={editorRef}
          placeholder="Write the article"
          ariaLabel="Article"
        />
      </Box>
    </Box>
  );
}

function FormFields({ form }: { form: Form }) {
  const set =
    (field: keyof AppPostFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      form.setValues((v) => ({ ...v, [field]: event.target.value }));
  return (
    <Stack spacing={2}>
      <TextField label="Title" size="small" value={form.values.title} onChange={set('title')} />
      <TextField
        label="Publish date"
        type="date"
        size="small"
        value={form.values.publishDate}
        onChange={set('publishDate')}
        InputLabelProps={{ shrink: true }}
      />
      <ArticleField form={form} />
      <TextField
        label="Author name"
        size="small"
        value={form.values.authorName}
        onChange={set('authorName')}
      />
      <ImageField form={form} />
    </Stack>
  );
}

/** Edits an app-only post in place on the News page, in place of the post list. */
export default function AppPostEditor(props: Props) {
  const form = useAppPostForm({ ...props, open: true });
  return (
    <Box sx={{ maxWidth: 900 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton aria-label="Back to news" onClick={props.onClose}>
          <ArrowBackIcon />
        </IconButton>
        <StyledHeading>{props.editing ? 'Edit app post' : 'Add app post'}</StyledHeading>
      </Stack>
      <StyledTypography sx={{ mb: 2, color: Palette.default.disabledText }}>
        A short, app-only article — no external link, readers stay in the app.
      </StyledTypography>
      <FormFields form={form} />
      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <FormActions form={form} onClose={props.onClose} />
      </Stack>
    </Box>
  );
}
